import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ProductCalculationResult } from 'src/app/services/calculator/models/calculator-product-result-model';
import { ProductImageModalComponent } from '../../modals/product-image-modal/product-image-modal.component';
import { Product } from 'src/app/services/calculator/models/calculator-product-result-model';
import { ProductAdvancedFilterModel } from 'src/app/services/product/models/product-advanced-filter-model';
import { TypeProductEnum } from '../../../shared/enums/type-product.enum';
import { GenericNavDrawerComponent } from '../../../shared/components/generic-nav-drawer/generic-nav-drawer.component';
import { PetModel } from 'src/app/services/pet/models/pet-model';
import { UntypedFormGroup } from '@angular/forms';
import { AgeGroupEnum } from 'src/app/modules/shared/enums/age-group.enum';
import { PhysicalActivityEnum } from 'src/app/modules/shared/enums/physical-activity.enum';
import { OrientationPurposeEnum } from 'src/app/modules/shared/enums/orientation-purpose.enum';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ClinicalCharacteristicEnum } from 'src/app/modules/shared/enums/clinical-characteristic.enum';

@Component({
  selector: 'app-food-suggestion-card',
  templateUrl: './food-suggestion-card.component.html',
  styleUrl: './food-suggestion-card.component.scss'
})
export class FoodSuggestionCardComponent implements OnInit, OnChanges {
  @Input() enabled: boolean = false;
  @Input() checked: boolean = false;
  @Input() isCompleteFood: boolean = false;
  @Input() foodType: 'dry' | 'wet' | 'cookie' | 'snack' = 'dry';
  @Input() speciePet: number = 1;
  @Input() isCookieSelected: boolean = false;
  @Input() isSnackWetFoodSelected: boolean = false;
  @Input() petSelected: PetModel;
  @Input() nutritionalGuidanceForm: UntypedFormGroup;
  @Input() product: ProductCalculationResult;
  @Input() isRecalculatingValues: boolean = false;

  @Output() productSelected = new EventEmitter<Product>();
  @Output() checkboxChanged = new EventEmitter<boolean>();
  @Output() portionsChanged = new EventEmitter<{
    portions: number;
    grams: number;
  }>();

  @ViewChild('drawer') drawer: GenericNavDrawerComponent;

  originalGramsPerDay: number = 0;

  mealsPerDay: number = 0;
  gramsPerMeal: number = 0;
  wholeGrams: number = 0;

  titleProductToChange: string = '';
  productToChangeFilter: ProductAdvancedFilterModel = {};
  filterKeyProductToChange: string = '';

  private readonly AdultOrSenior = [AgeGroupEnum.ADULT, AgeGroupEnum.SENIOR];
  private readonly WeightReduction = [
    PhysicalActivityEnum.WEIGHT_REDUCTION,
    PhysicalActivityEnum.WEIGHT_REDUCTION_RETURN
  ];
  private readonly ObesityTreatment = [
    OrientationPurposeEnum.CAT_OBESITY,
    OrientationPurposeEnum.DOG_OBESITY
  ];

  constructor(
    private modalService: BsModalService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    if (this.checked && this.isCompleteFood) {
      this.initializeManualValues();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['product'] &&
      !changes['product'].firstChange &&
      this.isCompleteFood
    ) {
      const hasInitializedManualValues =
        this.mealsPerDay > 0 && this.gramsPerMeal > 0 && this.wholeGrams > 0;

      this.originalGramsPerDay = this.product?.gramsPerDay || 0;

      if (this.checked && hasInitializedManualValues) {
        // When another food option (ex: "petisco") triggers a new calculator
        // result, we need to keep `mealsPerDay` as the user's selection and
        // recalculate `gramsPerMeal` so totals stay consistent.
        const safeWholeGrams = Math.max(1, this.originalGramsPerDay);

        let nextGramsPerMeal = this.roundToOneDecimal(
          safeWholeGrams / this.mealsPerDay
        );
        nextGramsPerMeal = Math.max(0.1, nextGramsPerMeal);

        this.wholeGrams = safeWholeGrams;
        this.gramsPerMeal = nextGramsPerMeal;
        this.emitPortionsChanged();
        return;
      }

      if (!this.checked) {
        this.initializeManualValues();
      }
    }
  }

  onCheckboxClick(): void {
    if (!this.enabled) return;

    const newCheckedState = !this.checked;
    this.checkboxChanged.emit(newCheckedState);

    if (newCheckedState && this.isCompleteFood) {
      this.initializeManualValues();
    }
  }

  private initializeManualValues(): void {
    if (this.product) {
      this.originalGramsPerDay = this.product.gramsPerDay;
      this.mealsPerDay = this.product.suggestedPortionAmount || 1;
      const gramsPerDay = Math.max(0, this.product.gramsPerDay);
      this.wholeGrams = Math.max(1, gramsPerDay || 1);
      let gramsPerMeal = this.roundToOneDecimal(
        this.wholeGrams / this.mealsPerDay
      );
      this.gramsPerMeal = Math.max(0.1, gramsPerMeal);
    }
  }

  private roundToOneDecimal(value: number): number {
    return Math.round(value * 10) / 10;
  }

  private formatCompleteFoodDailyGrams(value: number): string {
    const rounded = this.roundToOneDecimal(value);
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  canInteract(): boolean {
    return this.checked;
  }

  canOpenBenefits(): boolean {
    return this.enabled && !!this.product?.product?.productUrl;
  }

  canChangeProduct(): boolean {
    const isNcObesityProduct =
      this.product?.product?.clinicalConditionId ===
      ClinicalCharacteristicEnum.OBESITY;
    const isPetAdultOrSenior = this.AdultOrSenior.includes(
      this.petSelected?.faixaEtariaId
    );
    const isObjectiveWeightReduction = this.WeightReduction.includes(
      this.nutritionalGuidanceForm.controls.nivelAtividade?.value
    );
    const isPurposeObesity = this.ObesityTreatment.includes(
      this.nutritionalGuidanceForm.controls.orientationPurposeId?.value
    );

    return !(
      isPetAdultOrSenior &&
      isPurposeObesity &&
      isObjectiveWeightReduction &&
      isNcObesityProduct
    );
  }

  expandProductInModal(): void {
    if (this.enabled && this.product?.product?.productImageUrl) {
      this.modalService.show(ProductImageModalComponent, {
        class: 'modal-lg',
        initialState: {
          productName: this.product.product.name,
          productImage: this.product.product.productImageUrl
        }
      });
    }
  }

  validatePortion(): void {
    if (this.mealsPerDay < 1) {
      this.mealsPerDay = 1;
      this.divideGrams(1);
      return;
    }

    if (this.mealsPerDay > 6) {
      this.mealsPerDay = 6;
      this.divideGrams(6);
      return;
    }

    this.divideGrams(this.mealsPerDay);
  }

  updatePortion(type: string, value?: number): void {
    if (value) {
      this.mealsPerDay = value;
      this.divideGrams(value);
      return;
    }

    if (type === 'up') {
      this.mealsPerDay = this.mealsPerDay + 1;
      this.divideGrams(this.mealsPerDay);
    } else {
      this.mealsPerDay = this.mealsPerDay - 1;
      this.divideGrams(this.mealsPerDay);
    }
  }

  validateGrams(): void {
    this.gramsPerMeal = this.roundToOneDecimal(this.gramsPerMeal);
    this.updateWholeGrams();
    if (this.gramsPerMeal < 0.1) {
      this.gramsPerMeal = 0.1;
      this.updateWholeGrams();
    }

  }

  updateWholeGrams(value?: number): void {
    if (value !== undefined) {
      this.wholeGrams = value;
      return;
    }

    const total = this.roundToOneDecimal(
      this.gramsPerMeal * this.mealsPerDay
    );
    this.wholeGrams = total;
    this.emitPortionsChanged();
  }

  divideGrams(value: number): void {
    let nextGramsPerMeal = this.roundToOneDecimal(
      this.wholeGrams / value
    );
    nextGramsPerMeal = Math.max(0.1, nextGramsPerMeal);
    this.gramsPerMeal = nextGramsPerMeal;
    this.emitPortionsChanged();
  }

  updateGrams(type: string): void {
    const value = this.gramsPerMeal;

    if (type === 'up') {
      this.gramsPerMeal = value + 10;
    } else {
      if (value - 10 < 1) {
        this.gramsPerMeal = 1;
        this.updateWholeGrams(1);
        return;
      } else {
        this.gramsPerMeal = value - 10;
      }
    }

    this.updateWholeGrams();
  }

  decrementValue(
    value: number,
    onChange: (value: number) => void,
    step: number = 1
  ): void {
    const next = this.roundToOneDecimal(value - step);
    if (step < 1) {
      if (next >= 0.1) {
        onChange(next);
      }
    } else if (next >= 1) {
      onChange(next);
    }
  }

  incrementValue(
    value: number,
    onChange: (value: number) => void,
    step: number = 1,
    max?: number
  ): void {
    const next = this.roundToOneDecimal(value + step);
    if (max === undefined || next <= max) {
      onChange(next);
    }
  }

  onInputChange(event: Event, onChange: (value: number) => void): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);
    if (!isNaN(value) && value >= 1) {
      onChange(value);
    }
  }

  onGramsInputChange(event: Event, onChange: (value: number) => void): void {
    const raw = (event.target as HTMLInputElement).value.replace(',', '.');
    const value = parseFloat(raw);
    if (!isNaN(value) && value >= 0.1) {
      onChange(this.roundToOneDecimal(value));
    }
  }

  updateMealsPerDay(value: number): void {
    this.mealsPerDay = value;
    this.validatePortion();
  }

  updateGramsPerMeal(value: number): void {
    this.gramsPerMeal = value;
    this.validateGrams();
  }

  private emitPortionsChanged(): void {
    this.portionsChanged.emit({
      portions: this.mealsPerDay,
      grams: this.gramsPerMeal
    });
  }

  getIconSrc() {
    const theme = this.checked ? 'primary' : 'dark';
    if (this.foodType === 'dry') {
      return `../../../assets/icons/dry-food-icon-${theme}.svg`;
    } else if (this.foodType === 'wet') {
      return `../../../assets/icons/wet-food-icon-${theme}.svg`;
    } else if (this.foodType === 'cookie') {
      return `../../../assets/icons/cookie-icon-${theme}.svg`;
    } else if (this.foodType === 'snack') {
      return `../../../assets/icons/cookie-icon-${theme}.svg`;
    }
    return '';
  }

  getPotionBadgeIconSrc() {
    const theme = this.checked ? 'dark' : 'medium';
    if (this.foodType === 'cookie') {
      return `../../../assets/icons/cookie-icon-${theme}.svg`;
    }
    return `../../../assets/icons/food-icon-${theme}.svg`;
  }

  getTitle() {
    if (this.foodType === 'dry') {
      return 'Alimento Seco';
    } else if (this.foodType === 'wet') {
      return 'Alimento Úmido';
    } else if (this.foodType === 'cookie') {
      return 'Cookies';
    } else if (this.foodType === 'snack') {
      return 'Petisco Úmido';
    }
    return '';
  }

  getOriginalGramsPerDay(option: 'only-value' | 'with-label'): string {
    const label = option === 'with-label' ? 'g /dia' : '';
    if (this.isCompleteFood) {
      const v = this.formatCompleteFoodDailyGrams(this.wholeGrams);
      return option === 'with-label' ? v + label : v;
    }
    return this.originalGramsPerDay + label;
  }

  getRecommendedPortionAmount(option: 'only-value' | 'with-label') {
    const label =
      option === 'with-label'
        ? this.foodType === 'cookie'
          ? ' /dia'
          : 'g /dia'
        : '';

    if (this.isCompleteFood) {
      const v = this.formatCompleteFoodDailyGrams(this.wholeGrams);
      return option === 'with-label' ? v + label : v;
    }

    if (this.foodType === 'dry') {
      return this.product.gramsPerDay + label;
    } else if (this.foodType === 'wet') {
      return this.product.gramsPerDay + label;
    } else if (this.foodType === 'cookie') {
      const hasCookieAndWetSnack =
        this.isCookieSelected && this.isSnackWetFoodSelected;
      const value = hasCookieAndWetSnack
        ? this.product.cookieAndWetUnitaryValue
        : this.product.cookieUnitaryValue;
      return this.formatCookieValue(value, option) + label;
    } else if (this.foodType === 'snack') {
      const hasCookieAndWetSnack =
        this.isCookieSelected && this.isSnackWetFoodSelected;
      const value = hasCookieAndWetSnack
        ? this.product.cookieAndWetGramsValue
        : this.product.wetGramsValue;
      return value + label;
    }
    return 0;
  }

  private formatCookieValue(
    value: number,
    option: 'only-value' | 'with-label'
  ): string {
    // Se o valor for menor que 1, retorna 0.5
    if (value < 1) {
      return option === 'with-label' ? '½' : '0.5';
    }

    // Se o valor for inteiro, retorna o valor
    if (value === Math.floor(value)) {
      return value.toString();
    }

    // Para valores com decimal, sempre arredonda para 0.5
    const integerPart = Math.floor(value);

    if (option === 'with-label') {
      return integerPart > 0 ? `${integerPart} e ½` : '½';
    } else {
      return (integerPart + 0.5).toString();
    }
  }

  openChangeProductModal(): void {
    if (!this.canInteract()) return;
    if (!this.canChangeProduct()) {
      this.notificationService.warning(
        'A troca do alimento NC Obesidade está desabilitada para tratamentos com foco em redução de peso.'
      );
      return;
    }

    this.prepareChangeProductData();
    this.drawer.toggleVisibility();
  }

  private prepareChangeProductData(): void {
    switch (this.foodType) {
      case 'dry':
        this.titleProductToChange = 'Alimento Seco';
        this.productToChangeFilter = {
          typeProduct: TypeProductEnum.DRY_FOOD,
          specie: this.speciePet,
          isCompleteFood: true
        };
        this.filterKeyProductToChange = 'dry';
        break;
      case 'wet':
        this.titleProductToChange = 'Alimento Úmido';
        this.productToChangeFilter = {
          typeProduct: TypeProductEnum.WET_FOOD,
          specie: this.speciePet,
          isCompleteFood: true
        };
        this.filterKeyProductToChange = 'wet';
        break;
      case 'cookie':
        this.titleProductToChange = 'Cookie';
        this.productToChangeFilter = {
          typeProduct: TypeProductEnum.COOKIE,
          specie: this.speciePet,
          isCompleteFood: false
        };
        this.filterKeyProductToChange = 'cookie';
        break;
      case 'snack':
        this.titleProductToChange = 'Petisco Úmido';
        this.productToChangeFilter = {
          typeProduct: TypeProductEnum.WET_FOOD,
          specie: this.speciePet,
          isCompleteFood: false
        };
        this.filterKeyProductToChange = 'snack';
        break;
    }
  }

  onProductSelected(product: Product): void {
    this.productSelected.emit(product);
  }
}
